import { EChartsOption } from "echarts";

import { Device, MonitoringType, ProductModelService, System } from "../models";
import { AHheatPumpRTData, ConnextConfig, ConnextData, HeatPumpDAY, Inverter, InverterV2, LoggerEnumType } from "../telemetry";
import { DeviceType } from "../models/firebase.model";
import { EnergySeries, EnergySeriesWithZeros } from "../telemetry-graph";

export const countInverter = (system: System) => system.devices.filter((device) => device.type === 'inverter').length;
export const hasConnext = (system: System) => !!system.configuration.connextId;
export const countEvcharge = (system: System) => system.devices.filter((device) => device.type === 'evcharge').length;
export const countEnerclic = (system: System) => system.devices.filter((device) => device.type === 'enerclic').length;
export const countMeter = (system: System) => system.devices.filter((device) => device.type === 'meter').length;
export const countHybrid = (system: System) => system.devices.filter((device) => {
  const product = new ProductModelService(device.id);
  return product.type?.name === 'inverter' && (
    product.type?.value === 'hybrid' ||
    product.type?.value === 'hybrid-3ph'
  );
}).length;
export const countStorage = (system: System) => system.devices.filter((device) => {
  const product = new ProductModelService(device.id);
  return product.type?.name === 'inverter' && product.type?.value === 'storage';
}).length;
export const count3Phase = (system: System) => system.devices.filter((device) => {
  const product = new ProductModelService(device.id);
  return product.type?.name === 'inverter' && product.type?.phase === 'three-phase';
}).length;

export const isProductHybrid3Phase = (product: ProductModelService) =>
  product.type?.name === 'inverter' && product.type.value === 'hybrid-3ph';
export const isProductHybrid = (product: ProductModelService) =>
  product.type?.name === 'inverter' && (product.type.value === 'hybrid-3ph' || product.type.value === 'hybrid');
export const isProductStorage = (product: ProductModelService) =>
  product.type?.name === 'inverter' && product.type.value === 'storage';
export const isProductMeter = (product: ProductModelService) =>
  product.type?.name === 'meter';
export const isProductEnerclic = (product: ProductModelService) =>
  product.type?.name === 'enerclic';

export const deviceTypes = (devices: Device[]) => ({
  isThereSensor: devices.some((d) => (new ProductModelService(d.id)).type?.name === 'no-zcs'),
  isThereInverter: devices.some((d) => (new ProductModelService(d.id)).type?.name === 'inverter'),
});

export const countDevicesFromState = (devices: Device[]) => devices.filter((device) =>
  ['inverter', 'meter', 'evcharge' , 'enerclic'].includes(device.type)).length;
export const countEvchargeFromState = (devices: Device[]) => devices.filter((device) => device.type === 'evcharge').length;

export const checkFwVersion = (currentFwVersion?: string, minFwVersion = '0') => {
  if (!currentFwVersion) {
    return false;
  }

  const currentParts = currentFwVersion.split('.');

  // Determina la versione minima in base al formato della versione corrente
  let actualMinFwVersion = minFwVersion;
  if (minFwVersion === '0') {
    actualMinFwVersion = currentParts.length === 3 ? '1.30.1463' : '200.300';
  }

  const minParts = actualMinFwVersion.split('.');

  // Se entrambe hanno 2 parti, confronta come versioni a 2 cifre
  if (currentParts.length === 2 && minParts.length === 2) {
    const pMinFw = Number(minParts.map((d) => d.padStart(6, '0')).join(''));
    const pCurrFw = Number(currentParts.map((d) => d.padStart(6, '0')).join(''));
    return pCurrFw >= pMinFw;
  }

  // Se entrambe hanno 3 parti, confronta come versioni a 3 cifre
  if (currentParts.length === 3 && minParts.length === 3) {
    const pMinFw = Number(minParts.map((d) => d.padStart(6, '0')).join(''));
    const pCurrFw = Number(currentParts.map((d) => d.padStart(6, '0')).join(''));
    return pCurrFw >= pMinFw;
  }

  // Se i formati non corrispondono, considera non compatibile
  return false;
};

export const checkIfMissingConfigParameters = (config?: Partial<ConnextConfig>) => {
  if (!config) {
    return null;
  }
  if ([null, undefined].includes(config.pType as any)) {
    return true;
  }
  if ([null, undefined].includes(config.maxLoadPwr as any)) {
    return true;
  }
  if ([null, undefined].includes(config.TZ as any)) {
    return true;
  }
  return false;
};

export enum UserPermissions {
  Installer = 3,
  Owner = 2,
  Admin = 1,
  viewer = 0,
  hidden = -1,
}

export const isEditable = (connextData: Partial<ConnextData>, amIOperator = false) =>
  isConnected(connextData) && isAuthorized(connextData, amIOperator);

export const isConnected = (connextData?: Partial<ConnextData> | null) =>
  connextData?.info?.connection.status === 'connected';

export const isAuthorized = (connextData?: Partial<ConnextData> | null, amIOperator = false) => {
  const ownerOrAdmin = [UserPermissions.Owner, UserPermissions.Admin].includes(connextData?.userLevel || UserPermissions.viewer);
  return ownerOrAdmin || amIOperator;
};

export const isOwner = (connextData: Partial<ConnextData>) =>
  [UserPermissions.Owner].includes(connextData.userLevel || UserPermissions.viewer);

export const isAdmin = (connextData: Partial<ConnextData>) =>
  [UserPermissions.Admin].includes(connextData.userLevel || UserPermissions.viewer);

export  const cmpConnextId = (s1?: string, s2?: string) => {
  if (s1 === undefined || s2 === undefined) {
    return false;
  }
  return s1.toLowerCase() === s2.toLowerCase();
};

export const MAX_PLANT_LIMITS = {
  max: 500,
  min: 2.5,
};

export const hasLanOrGsm = (config?: number) => config === undefined || config === null
  ? null
  // eslint-disable-next-line no-bitwise
  : (config & 1) === 1
    ? 'GSM'
    : 'LAN';

export const isProductionId = (id: string) => id.length !== 24;

export const handleInverterPowerValues = (value: number, key: string) => {
  if (['P3', 'P6'].includes(key)) {
    return -value;
  }
  return value;
};

export const getMeterSerial = (id: string) => {
  if (id.endsWith('-00001')) {
    return id;
  }
  return id.concat('-00001');
};

export const maskIdMeter = (id: string) => id.replace(/(\d+)\-(\d+)/g, 'meter$1A$2');
export const removeMaskIdMeter = (id: string) => id.replace(/meter(\d+)A(\d+)(meterEnergyQuery|meterPowerQuery)?/g, '$1');

export const maskIdHeatpump = (id: string) => id.replace(/(\d{21})/g, 'hp_$1');
export const removeMaskIdHeatpump = (id: string) => id.replace(/hp_(\d{21})/g, '$1');

export const removeHeatpumpMaskFromDeviceList = <T extends Partial<AHheatPumpRTData> | Array<Partial<AHheatPumpRTData>> | Partial<HeatPumpDAY> | Array<Partial<HeatPumpDAY>>>
  (devices: { [key: string]: T }) => {
  const cleanCt: { [key: string]: T } = {};
  for (const key in devices) {
    if (Object.prototype.hasOwnProperty.call(devices, key)) {
      const element = devices[key];
      cleanCt[removeMaskIdHeatpump(key)] = element;
    }
  }
  return cleanCt;
}

export const maskEmsIfNeeded = (id: string) => {
  if (id.startsWith('ZZ') && id.endsWith('Z')) {
    return id;
  }
  return `ZZ${id}Z`;
};

export const removeMaskConnextId = (id: string) => id.replace(/ZZ([A-Za-z0-9]{24})Z/g, '$1');

export const removeMaskAzzurroHubId = (id: string) => id.replace(/ZZ([A-Za-z0-9]{12})Z/g, '$1');

export const removeMaskIdFromDeviceList = <T extends Partial<Inverter> | Array<Partial<Inverter>> >
  (devices: { [key: string]: T }) => {
  const cleanCt: { [key: string]: T } = {};
  for (const key in devices) {
    if (Object.prototype.hasOwnProperty.call(devices, key)) {
      const element = devices[key];
      cleanCt[removeMaskIdMeter(key)] = element;
    }
  }
  return cleanCt;
};

export const removeAllMaskIdFromDeviceList = <T extends Partial<Inverter> | Partial<{plantId: string}> | Array<Partial<Inverter> | Array<Partial<InverterV2>>> >
  (devices: { [key: string]: T }) => {
  const cleanCt: { [key: string]: T } = {};
  for (const key in devices) {
    if (Object.prototype.hasOwnProperty.call(devices, key)) {
      const element = devices[key];
      const newKey = removeMaskIdMeter(removeMaskConnextId(removeMaskAzzurroHubId(key)));
      cleanCt[newKey] = element;
    }
  }
  return cleanCt;
};

export const removeEmptyDevicesFromDeviceList = <T extends Partial<Inverter> | Array<Partial<Inverter>> >
(devices: { [key: string]: T }) => {
  const cleanCt: { [key: string]: T } = {};
  for (const key in devices) {
    if (Object.prototype.hasOwnProperty.call(devices, key)) {
      const element = devices[key];
      if (Array.isArray(element) && element.length > 0) {
        cleanCt[key] = element;
      } else if (!Array.isArray(element)) {
        cleanCt[key] = element;
      }
    }
  }
  return cleanCt;
};

export const CompatibleWith = (firmwareCategory?: number) => {
  let compatibleWith = 'AzzurroMonitoring';
  if (firmwareCategory === 2) {
    compatibleWith = 'GridX';
  }
  return compatibleWith;
}

export type LegendInverterTelemetry = {
  P_DC_TOT?: boolean;
  P_EXP?: boolean;
  P_LOAD?: boolean;
  P_B_AGGR?: boolean;
};

export const hasTigo = (system: System) => {
  const tigoId  = system.configuration.tigoId;
  if (tigoId !== undefined && tigoId !== null) {
    return true;
  }
  return false;
};

export const getAggregateType = (type: DeviceType, inverterGwType: MonitoringType): string => {
  switch (type) {
    case "inverter":
      return inverterGwType === 'Connext'
        ? 'Connext' : inverterGwType === 'AzzurroHub'
          ? 'Azzurro_Hub' : 'Solarman';
    case "azzurro-hub":
      return 'Azzurro_Hub';
    case "enerclic":
      return 'Combox';
    case "meter":
      return 'Meter';
    case "connext":
      return 'Connext';
  }

  return '';
}

export const getRangeDataV2Type = (type: DeviceType, inverterGwType: MonitoringType): LoggerEnumType => {
switch (type) {
    case "inverter":
      return inverterGwType === 'Connext'
        ? LoggerEnumType.Connext : inverterGwType === 'AzzurroHub'
          ? LoggerEnumType.Azzurro_Hub : LoggerEnumType.Solarman;
    case "enerclic":
      return LoggerEnumType.Combox;
    case "azzurro-hub":
      return LoggerEnumType.Azzurro_Hub;
    case "meter":
      return LoggerEnumType.Meter;
    case "connext":
    case "no-zcs":
      return LoggerEnumType.Connext;
  }

  return LoggerEnumType.Solarman;
}

export const updateSelectedAndAxisLimits = (option: EChartsOption, selected: LegendInverterTelemetry) => {
  const DEFAULT_Y_AXIS_MAX = 2000;
  let updatedOptions: EChartsOption = { ...option };
  const selectedKeys = (Object.keys(selected) as Array<keyof typeof selected>).filter((key) => selected[key]);
  if (Array.isArray(option?.series)) {
    const extractValues = (data: number[][]) => data.map((d) => d[1]);
    const series = option?.series;
    const maxValue = series?.filter((s) => selectedKeys.includes(s.name as keyof typeof selected))
      .reduce((acc, s) => Math.max(acc, Math.max(...extractValues(s.data as number[][]))), 0) ?? 0;
    const minValue = series?.filter((s) => selectedKeys.includes(s.name as keyof typeof selected))
      .reduce((acc, s) => Math.min(acc, Math.min(...extractValues(s.data as number[][]))), 0) ?? 0;

    const maxTotalValue = maxValue !== 0 || minValue !== 0 ?  Math.max(maxValue, Math.abs(minValue)) * 1.1 : DEFAULT_Y_AXIS_MAX;
    updatedOptions = {
      ...updatedOptions,
      legend : {
        ...updatedOptions.legend,
        selected,
      },
      yAxis: {
        ...updatedOptions.yAxis,
        min: -maxTotalValue,
        max: maxTotalValue,
      },
    };
  }
  return updatedOptions;
};

export const removeNullToSeries = (series: EnergySeries): EnergySeriesWithZeros => {
  const seriesWithZeros: EnergySeriesWithZeros = {};
  for (const key in series) {
    if (Object.prototype.hasOwnProperty.call(series, key)) {
      const element = series[key];
      if (element) {
        seriesWithZeros[key] = {
          ...element,
          values: element.values.map((week) => week.map((day) => day ?? 0)),
        };
      }
    }
  }
  return seriesWithZeros;
};

export const parseSKonAggregateDailies = (input?: string): string | null => {
  const prefix = 'DAY#';
  if (input?.startsWith(prefix)) {
    return input.slice(prefix.length);
  }
  return null;
};
